import React from 'react';
import { useHelpOptional } from './HelpProvider';
import { howToByKey } from './content';

interface Props {
  /** The How To to offer, e.g. 'posts.publish-post'. */
  guideKey: string;
  /** The question, in the user's words. Defaults to the How To's own title. */
  label?: string;
  /** 'inline' sits inside a sentence or an error message; 'pill' stands alone. */
  variant?: 'pill' | 'inline';
  className?: string;
  /**
   * How to open it. The default previews the guide, which is right for a pill
   * sitting in the app. A step's see-also passes `followSeeAlso` instead, so
   * the detour remembers the step that sent the user and can hand them back.
   */
  onFollow?: (guideKey: string) => void;
}

/**
 * Help offered at the point of friction: a question the app asks on the user's
 * behalf, at the moment it occurs to them.
 *
 * Renders NOTHING when there is no help provider (tests, hosts outside it), or
 * when this user cannot see that How To — a guide gated on the Reporting role
 * must not be dangled in front of staff who would only find it closed to them.
 * So a caller can place one without repeating the role check that already lives
 * in the content.
 *
 * It opens the How To's preview rather than starting it: the user is mid-task
 * and deserves to see what they are agreeing to before a guide starts pointing
 * at things.
 */
const HelpAsk: React.FC<Props> = ({ guideKey, label, variant = 'pill', className, onFollow }) => {
  const help = useHelpOptional();
  if (!help) return null;
  if (!help.visible.some(h => h.key === guideKey)) return null;

  const text = label ?? howToByKey(guideKey)?.title ?? 'How do I do this?';

  return (
    <button
      type="button"
      className={`help-ask${variant === 'inline' ? ' help-ask--inline' : ''}${className ? ` ${className}` : ''}`}
      onClick={() => (onFollow ? onFollow(guideKey) : help.previewHowTo(guideKey))}
    >
      <i className="fas fa-circle-question" aria-hidden />
      {text}
    </button>
  );
};

export default HelpAsk;
